import * as THREE from 'three';
import { makeAk47Mag, makeLiveShell, makeMp5Mag, makePistolMag } from '../player/ProcWeapons';

export type ItemPickupKind =
  | 'pistol_clip'
  | 'shells'
  | 'mp5_clip'
  | 'ak_clip'
  | 'grenades'
  | 'rockets'
  | 'medi_small'
  | 'medi_large'
  | 'armor'
  | 'superhealth';

const texLoader = new THREE.TextureLoader();

function loadMap(url: string, rx: number, ry: number, srgb: boolean): THREE.Texture {
  const tex = texLoader.load(url);
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(rx, ry);
  tex.anisotropy = 8;
  return tex;
}

interface ItemMats {
  wood: THREE.MeshStandardMaterial;
  steel: THREE.MeshStandardMaterial;
  steelDark: THREE.MeshStandardMaterial;
  olive: THREE.MeshStandardMaterial;
  canvas: THREE.MeshStandardMaterial;
  strap: THREE.MeshStandardMaterial;
  brass: THREE.MeshStandardMaterial;
  rubber: THREE.MeshStandardMaterial;
}

let matsCache: ItemMats | null = null;

function itemMats(): ItemMats {
  if (matsCache) return matsCache;
  const woodAlbedo = loadMap('/assets/textures/WoodTable001/albedo.jpg', 1.4, 1.1, true);
  const woodRough = loadMap('/assets/textures/WoodTable001/roughness.jpg', 1.4, 1.1, false);
  const metalMap = loadMap('/assets/textures/Metal032/albedo.jpg', 2.2, 2.2, true);
  matsCache = {
    wood: new THREE.MeshStandardMaterial({
      map: woodAlbedo,
      roughnessMap: woodRough,
      color: 0xc4a070,
      metalness: 0.04,
      roughness: 0.62,
    }),
    steel: new THREE.MeshStandardMaterial({
      map: metalMap,
      color: 0x6a7278,
      metalness: 0.82,
      roughness: 0.32,
    }),
    steelDark: new THREE.MeshStandardMaterial({
      map: metalMap,
      color: 0x2a3036,
      metalness: 0.78,
      roughness: 0.4,
    }),
    olive: new THREE.MeshStandardMaterial({ color: 0x4a5a38, metalness: 0.08, roughness: 0.72 }),
    canvas: new THREE.MeshStandardMaterial({ color: 0xe8e0d4, metalness: 0.04, roughness: 0.78 }),
    strap: new THREE.MeshStandardMaterial({ color: 0x2a241c, metalness: 0.06, roughness: 0.8 }),
    brass: new THREE.MeshStandardMaterial({ color: 0xb08a3a, metalness: 0.85, roughness: 0.32 }),
    rubber: new THREE.MeshStandardMaterial({ color: 0x161616, metalness: 0.05, roughness: 0.84 }),
  };
  return matsCache;
}

function add(
  parent: THREE.Object3D,
  geo: THREE.BufferGeometry,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  rx = 0,
  ry = 0,
  rz = 0,
): THREE.Mesh {
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(x, y, z);
  mesh.rotation.set(rx, ry, rz);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  mesh.frustumCulled = false;
  parent.add(mesh);
  return mesh;
}

const canvasCache = new Map<string, THREE.CanvasTexture>();

function canvasTex(key: string, draw: (ctx: CanvasRenderingContext2D, s: number) => void): THREE.CanvasTexture {
  const hit = canvasCache.get(key);
  if (hit) return hit;
  const s = 128;
  const c = document.createElement('canvas');
  c.width = s;
  c.height = s;
  const ctx = c.getContext('2d')!;
  draw(ctx, s);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  canvasCache.set(key, tex);
  return tex;
}

function redCrossTex(): THREE.CanvasTexture {
  return canvasTex('cross-red', (ctx, s) => {
    ctx.fillStyle = '#f2eee6';
    ctx.fillRect(0, 0, s, s);
    ctx.fillStyle = '#c41e2a';
    const t = s * 0.18;
    const arm = s * 0.62;
    ctx.fillRect((s - t) * 0.5, (s - arm) * 0.5, t, arm);
    ctx.fillRect((s - arm) * 0.5, (s - t) * 0.5, arm, t);
  });
}

function goldCrossTex(): THREE.CanvasTexture {
  return canvasTex('cross-gold', (ctx, s) => {
    ctx.fillStyle = '#1a1814';
    ctx.fillRect(0, 0, s, s);
    ctx.fillStyle = '#f0d060';
    const t = s * 0.16;
    const arm = s * 0.7;
    ctx.fillRect((s - t) * 0.5, (s - arm) * 0.5, t, arm);
    ctx.fillRect((s - arm) * 0.5, (s - t) * 0.5, arm, t);
  });
}

function plateCamoTex(): THREE.CanvasTexture {
  return canvasTex('armor-plate', (ctx, s) => {
    ctx.fillStyle = '#5c666e';
    ctx.fillRect(0, 0, s, s);
    ctx.strokeStyle = '#4a545c';
    ctx.lineWidth = 1.6;
    const h = s / 6.2;
    for (let row = -1; row < 9; row++) {
      for (let col = -1; col < 8; col++) {
        const x = col * h + (row % 2) * h * 0.5;
        const y = row * h * 0.86;
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const a = (Math.PI / 3) * i - Math.PI / 6;
          const px = x + Math.cos(a) * h * 0.4;
          const py = y + Math.sin(a) * h * 0.4;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.stroke();
      }
    }
    ctx.fillStyle = '#2e8a96';
    ctx.beginPath();
    ctx.moveTo(s * 0.5, s * 0.3);
    ctx.lineTo(s * 0.36, s * 0.5);
    ctx.lineTo(s * 0.44, s * 0.5);
    ctx.lineTo(s * 0.5, s * 0.4);
    ctx.lineTo(s * 0.56, s * 0.5);
    ctx.lineTo(s * 0.64, s * 0.5);
    ctx.closePath();
    ctx.fill();
  });
}

function extrudePanel(shape: THREE.Shape, depth: number, bevel = false): THREE.BufferGeometry {
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: bevel,
    bevelThickness: bevel ? 0.0035 : 0,
    bevelSize: bevel ? 0.003 : 0,
    bevelSegments: 2,
    steps: 1,
    curveSegments: 12,
  });
  geo.translate(0, 0, -depth * 0.5);
  geo.computeVertexNormals();
  return geo;
}

/** Wearable vest outline — neck scoop + shoulders, not a rectangle. */
function vestShellShape(): THREE.Shape {
  const s = new THREE.Shape();
  s.moveTo(-0.07, -0.08);
  s.bezierCurveTo(-0.082, -0.08, -0.09, -0.02, -0.084, 0.028);
  s.lineTo(-0.076, 0.06);
  s.lineTo(-0.094, 0.078);
  s.lineTo(-0.1, 0.1);
  s.lineTo(-0.05, 0.114);
  s.lineTo(-0.026, 0.086);
  s.quadraticCurveTo(0, 0.058, 0.026, 0.086);
  s.lineTo(0.05, 0.114);
  s.lineTo(0.1, 0.1);
  s.lineTo(0.094, 0.078);
  s.lineTo(0.076, 0.06);
  s.lineTo(0.084, 0.028);
  s.bezierCurveTo(0.09, -0.02, 0.082, -0.08, 0.07, -0.08);
  s.quadraticCurveTo(0, -0.094, -0.07, -0.08);
  return s;
}

function chestPlateShape(): THREE.Shape {
  const s = new THREE.Shape();
  s.moveTo(-0.046, -0.04);
  s.lineTo(-0.05, 0.034);
  s.lineTo(-0.036, 0.05);
  s.lineTo(0.036, 0.05);
  s.lineTo(0.05, 0.034);
  s.lineTo(0.046, -0.04);
  s.closePath();
  return s;
}

function stackedMags(make: () => THREE.Group, n: number, gap: number): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < n; i++) {
    const mag = make();
    mag.position.x = (i - (n - 1) * 0.5) * gap;
    mag.rotation.z = (i - (n - 1) * 0.5) * 0.08;
    g.add(mag);
  }
  return g;
}

function buildPistolClip(): THREE.Group {
  return stackedMags(() => makePistolMag(), 2, 0.028);
}

function buildMp5Clip(): THREE.Group {
  const g = new THREE.Group();
  const mag = makeMp5Mag();
  mag.rotation.x = 0.12;
  g.add(mag);
  return g;
}

function buildAkClip(): THREE.Group {
  const g = new THREE.Group();
  const mag = makeAk47Mag();
  mag.rotation.z = 0.08;
  g.add(mag);
  return g;
}

function buildShellCrate(): THREE.Group {
  const m = itemMats();
  const g = new THREE.Group();
  add(g, new THREE.BoxGeometry(0.09, 0.012, 0.07), m.wood, 0, 0.006, 0);
  add(g, new THREE.BoxGeometry(0.09, 0.028, 0.008), m.wood, 0, 0.02, 0.031);
  add(g, new THREE.BoxGeometry(0.09, 0.028, 0.008), m.wood, 0, 0.02, -0.031);
  add(g, new THREE.BoxGeometry(0.008, 0.028, 0.054), m.wood, 0.041, 0.02, 0);
  add(g, new THREE.BoxGeometry(0.008, 0.028, 0.054), m.wood, -0.041, 0.02, 0);
  add(g, new THREE.BoxGeometry(0.094, 0.004, 0.074), m.steelDark, 0, 0.002, 0);

  const cols = 4;
  const rows = 2;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const shell = makeLiveShell();
      shell.position.set((c - 1.5) * 0.018, 0.02, (r - 0.5) * 0.022);
      shell.rotation.y = Math.PI * 0.5 + (c * 0.04 - 0.06);
      g.add(shell);
    }
  }
  return g;
}

function buildMedkit(large: boolean): THREE.Group {
  const m = itemMats();
  const g = new THREE.Group();
  const sx = large ? 1.28 : 1;
  const sy = large ? 1.18 : 1;
  const w = 0.11 * sx;
  const h = 0.055 * sy;
  const d = 0.078 * sx;

  add(g, new THREE.BoxGeometry(w, h, d), m.olive, 0, h * 0.5, 0);
  add(g, new THREE.BoxGeometry(w * 0.98, 0.006, d * 0.98), m.canvas, 0, h + 0.001, 0);

  const lid = new THREE.MeshStandardMaterial({
    map: redCrossTex(),
    color: 0xffffff,
    metalness: 0.04,
    roughness: 0.55,
  });
  const plate = add(g, new THREE.PlaneGeometry(w * 0.62, d * 0.62), lid, 0, h + 0.005, 0, -Math.PI / 2, 0, 0);
  plate.renderOrder = 1;

  add(g, new THREE.BoxGeometry(0.012, 0.01, d * 0.55), m.steel, 0, h + 0.008, 0);
  add(g, new THREE.TorusGeometry(0.016, 0.0035, 8, 16, Math.PI), m.steel, 0, h + 0.02, 0, Math.PI / 2, 0, 0);
  add(g, new THREE.BoxGeometry(0.01, 0.008, 0.016), m.steelDark, w * 0.42, h * 0.55, d * 0.48);
  add(g, new THREE.BoxGeometry(0.01, 0.008, 0.016), m.steelDark, -w * 0.42, h * 0.55, d * 0.48);
  add(g, new THREE.BoxGeometry(w * 0.92, 0.006, 0.01), m.strap, 0, h * 0.35, d * 0.52);
  if (large) {
    add(g, new THREE.BoxGeometry(0.01, h * 0.7, d * 0.92), m.strap, w * 0.38, h * 0.45, 0);
    add(g, new THREE.BoxGeometry(w * 0.5, 0.008, 0.012), m.steel, 0, 0.006, 0);
  }
  return g;
}

function buildArmor(): THREE.Group {
  const m = itemMats();
  const g = new THREE.Group();
  const kevlar = new THREE.MeshStandardMaterial({
    color: 0x3c4a34,
    metalness: 0.06,
    roughness: 0.8,
  });
  const plate = new THREE.MeshStandardMaterial({
    map: plateCamoTex(),
    color: 0xc8d0d4,
    metalness: 0.62,
    roughness: 0.4,
  });

  const cy = 0.1;
  add(g, extrudePanel(vestShellShape(), 0.02, true), kevlar, 0, cy, 0.042, -0.1, 0, 0);
  add(g, extrudePanel(vestShellShape(), 0.018, true), kevlar, 0, cy, -0.042, 0.1, 0, 0);
  add(g, extrudePanel(chestPlateShape(), 0.012, true), plate, 0, cy + 0.006, 0.056, -0.1, 0, 0);
  add(g, extrudePanel(chestPlateShape(), 0.01, true), m.steelDark, 0, cy + 0.004, -0.054, 0.1, 0, 0);

  for (const y of [-0.018, 0.012, 0.042]) {
    add(g, new THREE.BoxGeometry(0.078, 0.005, 0.008), m.steelDark, 0, cy + y, 0.064, -0.1, 0, 0);
  }

  add(g, new THREE.BoxGeometry(0.02, 0.05, 0.078), m.strap, 0.08, cy - 0.012, 0);
  add(g, new THREE.BoxGeometry(0.02, 0.05, 0.078), m.strap, -0.08, cy - 0.012, 0);
  add(g, new THREE.BoxGeometry(0.024, 0.016, 0.016), m.steel, 0.09, cy - 0.01, 0.018);
  add(g, new THREE.BoxGeometry(0.024, 0.016, 0.016), m.steel, -0.09, cy - 0.01, 0.018);
  add(g, new THREE.BoxGeometry(0.01, 0.012, 0.02), m.brass, 0.09, cy - 0.01, 0.028);
  add(g, new THREE.BoxGeometry(0.01, 0.012, 0.02), m.brass, -0.09, cy - 0.01, 0.028);

  const strapR = 0.046;
  const strapT = 0.01;
  add(g, new THREE.TorusGeometry(strapR, strapT, 8, 14, Math.PI), m.strap, 0.046, cy + 0.086, 0, 0, Math.PI / 2, 0);
  add(g, new THREE.TorusGeometry(strapR, strapT, 8, 14, Math.PI), m.strap, -0.046, cy + 0.086, 0, 0, Math.PI / 2, 0);
  add(g, new THREE.TorusGeometry(0.034, 0.008, 8, 18), m.strap, 0, cy + 0.088, 0, Math.PI / 2, 0, 0);

  const flap = new THREE.Shape();
  flap.moveTo(-0.032, 0);
  flap.lineTo(-0.026, -0.028);
  flap.lineTo(0.026, -0.028);
  flap.lineTo(0.032, 0);
  flap.closePath();
  add(g, extrudePanel(flap, 0.01, true), kevlar, 0, cy - 0.086, 0.03, -0.18, 0, 0);

  return g;
}

function buildSuperHealth(): THREE.Group {
  const m = itemMats();
  const g = new THREE.Group();
  const kit = buildMedkit(true);
  kit.scale.setScalar(1.08);
  g.add(kit);

  const gold = new THREE.MeshStandardMaterial({
    color: 0xf0d060,
    metalness: 0.55,
    roughness: 0.28,
    emissive: 0xaa8800,
    emissiveIntensity: 0.55,
  });
  const orb = new THREE.MeshStandardMaterial({
    map: goldCrossTex(),
    color: 0xfff2c8,
    metalness: 0.2,
    roughness: 0.35,
    emissive: 0x886600,
    emissiveIntensity: 0.4,
  });
  add(g, new THREE.SphereGeometry(0.028, 16, 12), orb, 0, 0.095, 0);
  add(g, new THREE.BoxGeometry(0.012, 0.046, 0.012), gold, 0, 0.095, 0);
  add(g, new THREE.BoxGeometry(0.046, 0.012, 0.012), gold, 0, 0.095, 0);
  add(g, new THREE.CylinderGeometry(0.018, 0.022, 0.008, 12), m.steel, 0, 0.068, 0);
  return g;
}

function buildGrenadeCrate(): THREE.Group {
  const m = itemMats();
  const g = new THREE.Group();
  add(g, new THREE.BoxGeometry(0.22, 0.1, 0.16), m.olive, 0, 0.05, 0);
  add(g, new THREE.SphereGeometry(0.035, 8, 6), m.steelDark, -0.05, 0.12, 0);
  add(g, new THREE.SphereGeometry(0.035, 8, 6), m.steelDark, 0.05, 0.12, 0);
  return g;
}

function buildRocketCrate(): THREE.Group {
  const m = itemMats();
  const g = new THREE.Group();
  add(g, new THREE.BoxGeometry(0.28, 0.1, 0.14), m.olive, 0, 0.05, 0);
  const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.22, 8), m.steelDark);
  tube.rotation.z = Math.PI / 2;
  tube.position.set(0, 0.12, 0);
  g.add(tube);
  return g;
}

export function buildItemPickup(kind: ItemPickupKind): THREE.Group {
  switch (kind) {
    case 'pistol_clip':
      return buildPistolClip();
    case 'mp5_clip':
      return buildMp5Clip();
    case 'ak_clip':
      return buildAkClip();
    case 'shells':
      return buildShellCrate();
    case 'grenades':
      return buildGrenadeCrate();
    case 'rockets':
      return buildRocketCrate();
    case 'medi_small':
      return buildMedkit(false);
    case 'medi_large':
      return buildMedkit(true);
    case 'armor':
      return buildArmor();
    case 'superhealth':
      return buildSuperHealth();
  }
}
